<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import ListSearch from "@/components/ListSearch.vue";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import JobListRow from "./JobListRow.vue";
import { useAuthStore } from "@/stores/AuthStore";
import { CloudArrowUpIcon, PlusCircleIcon } from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const { setBreadcrumb } = useBreadcrumbs();
const auth = useAuthStore();

const jobs = ref([]);
const referenceTypes = ref([]);
const availableVehicles = ref([]);
const vehiclesLoading = ref(false);
const vehiclesError = ref("");
const loading = ref(false);
const refreshing = ref(false);
const errorMessage = ref("");
const requestSequence = ref(0);

const scope = ref(route.query.scope === "past" ? "past" : "upcoming");
const statusFilter = ref(
  ["resourced", "unresourced"].includes(route.query.status)
    ? route.query.status
    : "all",
);
const typeFilter = ref(route.query.af ?? "all");
const page = ref(Math.max(1, Number(route.query.page) || 1));
const meta = ref({
  current_page: 1,
  last_page: 1,
  total: 0,
  from: null,
  to: null,
  status_counts: { all: 0, resourced: 0, unresourced: 0 },
  scope_counts: { upcoming: 0, past: 0 },
});

const typeOptions = computed(() => [
  { label: "All types", value: "all" },
  ...referenceTypes.value.map((type) => ({
    label: type.name ?? type.prefix,
    value: type.prefix?.toLowerCase(),
  })),
]);

const statusCounts = computed(() => meta.value.status_counts ?? {});
const scopeCounts = computed(() => meta.value.scope_counts ?? {});
const showPrev = computed(() => meta.value.current_page > 1);
const showNext = computed(() => meta.value.current_page < meta.value.last_page);

const syncUrl = () =>
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      scope: scope.value === "past" ? "past" : undefined,
      status: statusFilter.value === "all" ? undefined : statusFilter.value,
      af: typeFilter.value === "all" ? undefined : typeFilter.value,
      page: page.value > 1 ? page.value : undefined,
    },
  });

const loadJobs = async ({ silent = false } = {}) => {
  const sequence = ++requestSequence.value;
  if (silent && jobs.value.length) {
    refreshing.value = true;
  } else {
    loading.value = true;
  }
  errorMessage.value = "";

  try {
    const { data } = await apiClient.get("/api/job", {
      params: {
        scope: scope.value,
        status: statusFilter.value,
        prefix: typeFilter.value === "all" ? undefined : typeFilter.value,
        page: page.value,
        per_page: 25,
      },
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    if (sequence !== requestSequence.value) return;
    jobs.value = data?.data ?? [];
    meta.value = data?.meta ?? meta.value;
  } catch (error) {
    if (sequence !== requestSequence.value) return;
    console.error(error);
    errorMessage.value =
      error?.response?.data?.message ?? "Unable to load jobs.";
  } finally {
    if (sequence === requestSequence.value) {
      loading.value = false;
      refreshing.value = false;
    }
  }
};

const loadReferenceTypes = async () => {
  try {
    const { data } = await apiClient.get("/api/reference/types");
    referenceTypes.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
  }
};

const loadVehicles = async () => {
  if (vehiclesLoading.value) return;
  vehiclesLoading.value = true;
  vehiclesError.value = "";
  try {
    const { data } = await apiClient.get("/api/vehicle", {
      params: { active: true, per_page: 250 },
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    availableVehicles.value = (Array.isArray(data?.data) ? data.data : []).sort(
      (a, b) =>
        (a?.registrationNumber ?? "").localeCompare(
          b?.registrationNumber ?? "",
        ),
    );
  } catch (error) {
    console.error(error);
    availableVehicles.value = [];
    vehiclesError.value =
      error?.response?.data?.message ?? "Unable to load active vehicles. Try again.";
  } finally {
    vehiclesLoading.value = false;
  }
};

const changeFilters = async () => {
  page.value = 1;
  await syncUrl();
  await loadJobs({ silent: true });
};

const setScope = async (value) => {
  if (scope.value === value) return;
  scope.value = value;
  await changeFilters();
};

const setStatus = async (value) => {
  if (statusFilter.value === value) return;
  statusFilter.value = value;
  await changeFilters();
};

const changePage = async (difference) => {
  const nextPage = page.value + difference;
  if (nextPage < 1 || nextPage > meta.value.last_page) return;
  page.value = nextPage;
  await syncUrl();
  await loadJobs({ silent: true });
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const view = (reference) => {
  router.push({ name: "reference-view", params: { id: reference.id } });
  setBreadcrumb(reference.name);
};

onMounted(() => {
  setBreadcrumb("Jobs");
  loadJobs();
  loadReferenceTypes();
  loadVehicles();
});
</script>

<template>
  <div>
    <div class="box">
      <div class="flex flex-wrap items-center gap-3">
        <ListSearch
          class="min-w-[240px] flex-1"
          url="/api/reference/search?query="
          :output="(value) => `${value.txt} - ${value.name}`"
          @selected="view"
        />
        <div class="flex gap-2">
          <router-link
            to="/reference/create"
            class="btn accent inline-flex items-center gap-2"
            title="Create new job"
          >
            <PlusCircleIcon class="icon" />
            <span class="hidden sm:inline">New job</span>
          </router-link>
          <router-link
            to="/reference/import"
            class="btn accent inline-flex items-center gap-2"
            title="Import jobs"
          >
            <CloudArrowUpIcon class="icon" />
            <span class="hidden sm:inline">Import</span>
          </router-link>
        </div>
      </div>

      <div
        class="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4 dark:border-slate-700"
      >
        <div
          class="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-sm font-semibold dark:bg-slate-900/60"
        >
          <button
            type="button"
            class="rounded-md px-3 py-2 transition"
            :class="
              scope === 'upcoming'
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            "
            @click="setScope('upcoming')"
          >
            Upcoming
            <span class="ml-1 text-xs opacity-70">{{
              scopeCounts.upcoming ?? 0
            }}</span>
          </button>
          <button
            type="button"
            class="rounded-md px-3 py-2 transition"
            :class="
              scope === 'past'
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            "
            @click="setScope('past')"
          >
            Previous
            <span class="ml-1 text-xs opacity-70">{{
              scopeCounts.past ?? 0
            }}</span>
          </button>
        </div>

        <div
          class="flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs font-semibold dark:bg-slate-900/60"
        >
          <button
            v-for="option in [
              { value: 'all', label: 'All', count: statusCounts.all },
              {
                value: 'unresourced',
                label: 'Needs resources',
                count: statusCounts.unresourced,
              },
              {
                value: 'resourced',
                label: 'Resourced',
                count: statusCounts.resourced,
              },
            ]"
            :key="option.value"
            type="button"
            class="rounded-md px-3 py-2 transition"
            :class="
              statusFilter === option.value
                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
            "
            @click="setStatus(option.value)"
          >
            {{ option.label }} ({{ option.count ?? 0 }})
          </button>
        </div>

        <label
          class="ml-auto flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
        >
          <span>Type</span>
          <select
            v-model="typeFilter"
            class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 shadow-sm focus:border-slate-400 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            @change="changeFilters"
          >
            <option
              v-for="option in typeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>

      <div
        class="mt-3 flex items-center justify-between text-xs text-slate-500"
      >
        <span>
          {{
            scope === "past"
              ? "Most recent previous jobs first"
              : "Next job date first; unscheduled jobs follow"
          }}
        </span>
        <span v-if="refreshing" class="animate-pulse">Updating…</span>
        <span v-else-if="meta.total"
          >Showing {{ meta.from }}–{{ meta.to }} of {{ meta.total }}</span
        >
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200"
    >
      {{ errorMessage }}
      <button
        type="button"
        class="ml-2 font-semibold underline"
        @click="loadJobs()"
      >
        Retry
      </button>
    </div>

    <div
      v-if="vehiclesError"
      class="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
      role="alert"
    >
      <span>{{ vehiclesError }}</span>
      <button
        type="button"
        class="font-semibold underline disabled:opacity-60"
        :disabled="vehiclesLoading"
        @click="loadVehicles"
      >
        {{ vehiclesLoading ? "Retrying…" : "Retry" }}
      </button>
    </div>

    <div
      v-if="loading && jobs.length === 0"
      class="space-y-3"
      aria-label="Loading jobs"
    >
      <div v-for="index in 4" :key="index" class="box animate-pulse">
        <div class="grid gap-3 md:grid-cols-4">
          <div class="h-5 rounded bg-slate-200 dark:bg-slate-700"></div>
          <div class="h-5 rounded bg-slate-200 dark:bg-slate-700"></div>
          <div class="h-5 rounded bg-slate-200 dark:bg-slate-700"></div>
          <div class="h-5 rounded bg-slate-200 dark:bg-slate-700"></div>
        </div>
      </div>
    </div>

    <div
      v-else-if="jobs.length === 0 && !errorMessage"
      class="box text-sm text-slate-500"
    >
      {{
        statusFilter === "all"
          ? "No jobs found in this section."
          : "No jobs match the current resource filter."
      }}
    </div>

    <div v-else class="space-y-3" :aria-busy="refreshing">
      <JobListRow
        v-for="job in jobs"
        :key="job.id"
        :job="job"
        :vehicles="availableVehicles"
        :vehicles-loading="vehiclesLoading"
        :vehicles-error="vehiclesError"
        :can-override-vehicle-limit="auth.hasRole('super')"
        @changed="loadJobs({ silent: true })"
      />
    </div>

    <PrevNextButtons
      :showPrev="showPrev"
      :showNext="showNext"
      @prev="changePage(-1)"
      @next="changePage(1)"
    />
  </div>
</template>
